import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Users, Sparkles, AlertTriangle, Home as HomeIcon, ChevronLeft } from 'lucide-react';
import { useInfiniteFeedPosts } from '@/hooks/usePosts';
import PostCard from '@/components/PostCard';
import FeaturedMemoriesGrid from '@/components/FeaturedMemoriesGrid';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

type Filter = 'lost' | 'adoption' | 'milestone';

const KEYWORDS: Record<Filter, RegExp> = {
  lost: /\b(lost|missing|perdid[oa]|desapareci[dx]|extravia[dx])/i,
  adoption: /\b(adopt|adopci[óo]n|rehome|en adopci[óo]n)/i,
  milestone: /\b(milestone|birthday|cumplea[ñn]os|anniversary|aniversario)/i,
};

const Community = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = useInfiniteFeedPosts();
  const [filter, setFilter] = useState<Filter>('lost');

  const POST_TYPE_FOR_FILTER: Record<Filter, string> = {
    lost: 'lost_pet',
    adoption: 'adoption_post',
    milestone: 'milestone',
  };

  const posts = useMemo(() => {
    const all = data?.pages.flatMap((p: any) => p) ?? [];
    const community = all.filter((p: any) => p.post_type !== 'profile_photo');
    const re = KEYWORDS[filter];
    const targetType = POST_TYPE_FOR_FILTER[filter];
    return community.filter((p: any) => {
      if (p.post_type === targetType) return true;
      return re.test(p.caption || '');
    });
  }, [data, filter]);

  const chips: { id: Filter; label: string; icon: typeof Users; tone: string }[] = [
    { id: 'lost', label: t('community.filterLost', 'Lost Pet Alerts'), icon: AlertTriangle, tone: 'from-red-500 to-orange-600' },
    { id: 'adoption', label: t('community.filterAdoption', 'Adoption'), icon: HomeIcon, tone: 'from-teal-500 to-cyan-600' },
    { id: 'milestone', label: t('community.filterMilestone', 'Milestones'), icon: Sparkles, tone: 'from-amber-500 to-orange-500' },
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 pt-4 pb-24 space-y-6">
      <header className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/explore'))}
          aria-label={t('common.back', 'Back')}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-card border border-border hover:bg-muted active:scale-95 transition"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-md">
          <Users className="h-5 w-5" />
        </span>
        <div>
          <h1 className="text-xl font-bold leading-tight">
            {t('community.title', 'Community')}
          </h1>
          <p className="text-xs text-muted-foreground">
            {t('community.subtitle', 'Featured pets, adoption listings and lost pet alerts')}
          </p>
        </div>
      </header>

      {/* Featured Memories */}
      <section>
        <div className="flex items-center justify-between mb-3 px-1">
          <h2 className="text-sm font-bold">{t('landing.featuredMemories', 'See Featured Memories')}</h2>
          <span className="text-xs text-muted-foreground">{t('landing.todaySuperstars', "Today's pet superstars")}</span>
        </div>
        <FeaturedMemoriesGrid limit={9} />
      </section>

      {/* Filters */}
      <section>
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 no-scrollbar">
          {chips.map((c) => {
            const Icon = c.icon;
            const active = filter === c.id;
            return (
              <button
                key={c.id}
                onClick={() => setFilter(c.id)}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all border',
                  active
                    ? `text-white shadow-sm bg-gradient-to-r ${c.tone} border-transparent`
                    : 'bg-card text-foreground/70 border-border hover:bg-muted'
                )}
              >
                <Icon className="h-4 w-4" />
                {c.label}
              </button>
            );
          })}
        </div>
      </section>

      {/* Community Feed */}
      <section className="space-y-4">
        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="w-full h-80 rounded-2xl" />
          ))
        ) : posts.length === 0 ? (
          <div className="text-center py-10 text-sm text-muted-foreground">
            {t('community.noPosts', 'No community posts yet for this filter.')}
          </div>
        ) : (
          posts.map((post: any) => <PostCard key={post.id} post={post} />)
        )}
      </section>
    </div>
  );
};

export default Community;
